import type { NotificationMessage } from '@/services/globalSignalR';
import { globalSignalR } from '@/services/globalSignalR';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faGlobe,
  faRandom,
  faSensorAlert,
  faTable,
  faTimelineArrow,
} from '@fortawesome/sharp-solid-svg-icons';
import DOMPurify from 'dompurify';
import { Sidebar } from 'eone-ui/sidebar/sidebar';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Labels } from '@lib/sc-eone-ui/src/strings/strings-en-us';
import '../help/help-sidebar.css';

// Maps backend EventLogEntityType enum values to sidebar menu icons
// ConnectorInstance=1, DataSource=2, Process=3, RealtimeRenewal=4, RealtimeStaging=5, Schedule=6
const entityTypeIconMap: Record<number, { icon: IconDefinition; color: string }> = {
  1: { icon: faRandom, color: '#1890ff' }, // Connector
  2: { icon: faTable, color: '#722ed1' }, // Data Source
  3: { icon: faTimelineArrow, color: '#fa8c16' }, // Process
  4: { icon: faTable, color: '#722ed1' }, // Realtime Renewal (data source related)
  5: { icon: faTable, color: '#722ed1' }, // Realtime Staging (data source related)
  6: { icon: faClock, color: '#13c2c2' }, // Schedule
};

function getNotificationIcon(message: NotificationMessage): {
  icon: IconDefinition;
  color: string;
} {
  if (message.entityType && entityTypeIconMap[message.entityType]) {
    return entityTypeIconMap[message.entityType];
  }
  // Fallback for messages without entity type
  if (message.type === 'global') return { icon: faGlobe, color: '#1890ff' };
  return { icon: faSensorAlert, color: '#ff4d4f' };
}

// Maps EventEntryType enum to title color and font weight
// 0=Error, 1=Warning, 2=Information, 3=SuccessAudit, 4=FailureAudit
function getTitleStyle(eventEntryType?: number): { color: string; fontWeight?: number } {
  switch (eventEntryType) {
    case 0: // Error
      return { color: '#ff4d4f', fontWeight: 700 };
    case 1: // Warning
      return { color: '#fa8c16' };
    case 2: // Information
      return { color: '#333' };
    case 3: // SuccessAudit
      return { color: '#52c41a' };
    case 4: // FailureAudit
      return { color: '#ff4d4f' };
    default:
      return { color: '#333' };
  }
}

interface NotificationsProps {
  Open: boolean;
  OnClose: () => void;
}

export function Notifications({ Open, OnClose }: NotificationsProps) {
  const [messages, setMessages] = useState<NotificationMessage[]>([]);
  const navigate = useNavigate();

  // Set up persistent message listener (only once on mount)
  useEffect(() => {
    const unsubscribe = globalSignalR.onMessagesChange((newMessages) => {
      console.log('Notifications sidebar: Messages updated', newMessages.length);
      setMessages([...newMessages]);
    });

    return unsubscribe;
  }, []);

  // Sync messages when sidebar opens
  useEffect(() => {
    if (Open) {
      setMessages(globalSignalR.getMessages());
    }
  }, [Open]);

  // Refresh timestamps every 30 seconds so "just now" → "1m ago" etc.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!Open || messages.length === 0) return;
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, [Open, messages.length]);

  const handleMarkAsRead = (messageId: string) => {
    globalSignalR.markAsRead(messageId);
  };

  const handleLinkClick = (message: NotificationMessage) => {
    handleMarkAsRead(message.id);
    if (message.linkUrl) {
      navigate(message.linkUrl);
      OnClose();
    }
  };

  const formatTime = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (!Open) return null;

  return (
    <Sidebar HeaderTitle={Labels.Notifications} Close={OnClose} DataTestId="email-header">
      <div className="sc-help-article-container">
        {messages.length === 0 ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: '#666',
            }}
          >
            <div style={{ fontSize: '14px' }}>No notifications yet</div>
          </div>
        ) : (
          <div>
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: message.read ? 'transparent' : '#f8f9ff',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onClick={() => handleMarkAsRead(message.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = message.read ? '#f8f9fa' : '#f0f2ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = message.read ? 'transparent' : '#f8f9ff';
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      marginTop: '3px',
                      color: getNotificationIcon(message).color,
                    }}
                  >
                    <FontAwesomeIcon icon={getNotificationIcon(message).icon} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight:
                          getTitleStyle(message.eventEntryType).fontWeight ??
                          (message.read ? 400 : 600),
                        color: getTitleStyle(message.eventEntryType).color,
                        marginBottom: '4px',
                      }}
                    >
                      {message.title}
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        lineHeight: '1.4',
                        marginBottom: '6px',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.isHtml ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(message.content),
                          }}
                        />
                      ) : (
                        <span>{message.content}</span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#999',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{formatTime(message.timestamp)}</span>
                        {message.linkUrl && (
                          <a
                            style={{
                              color: '#1890ff',
                              cursor: 'pointer',
                              textDecoration: 'none',
                              fontSize: '11px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLinkClick(message);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                          >
                            {message.linkText || 'View'}
                          </a>
                        )}
                      </div>
                      {!message.read && (
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#1890ff',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Status */}
        <div
          style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '11px',
            color: '#666',
          }}
        >
          {globalSignalR.isConnected ? (
            <span style={{ color: '#52c41a' }}>Connected</span>
          ) : (
            <span style={{ color: '#ff4d4f' }}>Disconnected</span>
          )}
        </div>
      </div>
    </Sidebar>
  );
}
